import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, EmptyState, ErrorState, Icon, Select, Skeleton, Tooltip, toast } from "@sagot/ui";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions, useRouteAgentId } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { PageHeader } from "../shared/page-header";
import { useAgentList } from "../shared/queries";
import type { EvalRun, EvalSet } from "../shared/types";
import { UpgradeCard, isPlanGate } from "../shared/upgrade-card";
import { FailedCases } from "./failed-cases";
import { LatestRunCard } from "./latest-run-card";
import { NewSetDialog } from "./new-set-dialog";
import { CompareDialog, RunHistory } from "./run-history";
import { SetSheet } from "./set-sheet";
import { SetsTable } from "./sets-table";
import { useEvalMutations, useEvalSets, useRunHistory } from "./use-evals";

function newestSet(sets: readonly EvalSet[]): EvalSet | undefined {
  return [...sets].sort((a, b) => (b.last_run_at ?? "").localeCompare(a.last_run_at ?? ""))[0];
}

function useCan(agentId: string) {
  const p = usePermissions(agentId);
  const writable = !p.readOnly;
  return {
    run: writable && p.can("evaluations.run", { agentId }),
    manage: writable && p.can("evaluations.manage", { agentId }),
    editAgent: writable && p.can("agents.edit", { agentId }),
  };
}

/** Sets still running: queued in this tab and their latest run is not newer than the queue time. */
function runningSets(sets: readonly EvalSet[], queued: Readonly<Record<string, string>>): Set<string> {
  return new Set(
    Object.entries(queued)
      .filter(([id, at]) => {
        const set = sets.find((s) => s.id === id);
        return set ? (set.last_run_at ?? "") < at : false;
      })
      .map(([id]) => id),
  );
}

function LatestSection({ setId, setName, agentId, canManage, onCompare, between }: { setId: string; setName: string; agentId: string; canManage: boolean; onCompare: (a: EvalRun, b: EvalRun) => void; between: ReactNode }) {
  const { t } = useTranslation();
  const history = useRunHistory(setId, false);
  const m = useEvalMutations(agentId);
  const [latest, previous] = [...(history.data?.items ?? [])].sort((a, b) => b.number - a.number);
  if (history.isPending) return <><Skeleton className="h-80 rounded-card" />{between}</>;
  if (!latest) return <><EmptyState icon="flask" title={t("build.evaluations.latest.noRuns")} description={t("build.evaluations.latest.noRunsBody")} />{between}</>;
  return (
    <>
      <LatestRunCard run={latest} previous={previous} setName={setName} onCompare={onCompare} onExport={m.exportRun} />
      {between}
      <RunHistory setId={setId} setName={setName} onCompare={onCompare} />
      {latest.fail_count + latest.partial_count > 0 ? <FailedCases run={latest} agentId={agentId} canManage={canManage} /> : null}
    </>
  );
}

export function EvaluationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  const agentId = useRouteAgentId() ?? "";
  const can = useCan(agentId);
  const agents = useAgentList();
  const [queued, setQueued] = useState<Record<string, string>>({});
  const cached = useEvalSets(agentId).data?.items ?? [];
  const sets = useEvalSets(agentId, runningSets(cached, queued).size > 0);
  const m = useEvalMutations(agentId);
  const items = sets.data?.items ?? [];
  const running = runningSets(items, queued);
  const mark = (id: string, at: string) => { setQueued((q) => ({ ...q, [id]: at })); };
  const [selected, setSelected] = useState<string | null>(null);
  const [opened, setOpened] = useState<EvalSet | null>(null);
  const [creating, setCreating] = useState(false);
  const [pair, setPair] = useState<{ a: EvalRun; b: EvalRun } | null>(null);
  const agentList = agents.data?.items ?? [];
  const agentName = (id: string | null) => (id ? agentList.find((a) => a.id === id)?.name ?? "—" : t("build.evaluations.sets.allAgents"));
  const current = items.find((s) => s.id === selected) ?? newestSet(items);
  const run = (set: EvalSet) => {
    m.run.mutate(set.id, {
      onSuccess: (r) => { mark(set.id, r.created_at); toast.info(t("build.evaluations.sets.queued", { name: set.name })); },
      onError: (e) => { toast.error(message(e)); },
    });
  };
  const runAll = () => {
    m.runAll.mutate(undefined, {
      onSuccess: (r) => { r.runs.forEach((x) => { mark(x.set_id, x.created_at); }); toast.info(t("build.evaluations.runAllQueued", { count: r.runs.length })); },
      onError: (e) => { toast.error(message(e)); },
    });
  };
  const newButton = <Button onClick={() => { setCreating(true); }} disabled={!can.manage}><Icon name="plus" />{t("build.evaluations.newSetButton")}</Button>;
  const gated = sets.isError && isPlanGate(sets.error);
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title={t("build.evaluations.title")}
        subtitle={t("build.evaluations.subtitle")}
        actions={
          <>
            <Select aria-label={t("build.evaluations.agent")} className="h-9 w-52" value={agentId} onChange={(e) => void navigate(consoleRoutes.evaluations(summary.slug, e.target.value))}>
              {agentList.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
            <Button variant="secondary" onClick={runAll} disabled={!can.run || items.length === 0} loading={m.runAll.isPending}><Icon name="play" />{t("build.evaluations.runAll")}</Button>
            {can.manage ? newButton : <Tooltip content={t("build.noPermission")}><span>{newButton}</span></Tooltip>}
          </>
        }
      />
      {gated ? <UpgradeCard feature={t("build.evaluations.planFeature")} /> : null}
      {sets.isError && !gated ? <ErrorState title={t("build.loadError")} description={t("build.loadErrorBody")} onRetry={() => void sets.refetch()} retryLabel={t("common.retry")} /> : null}
      {sets.isPending ? <><Skeleton className="h-80 rounded-card" /><Skeleton className="h-48 rounded-card" /></> : null}
      {sets.data && items.length === 0 ? <EmptyState icon="list-checks" title={t("build.evaluations.sets.empty")} description={t("build.evaluations.sets.emptyBody")} action={can.manage ? newButton : undefined} /> : null}
      {current ? (
        <LatestSection
          key={current.id}
          setId={current.id}
          setName={current.name}
          agentId={agentId}
          canManage={can.manage}
          onCompare={(a, b) => { setPair({ a, b }); }}
          between={<SetsTable sets={items} agentName={agentName} selectedId={current.id} runningIds={running} canRun={can.run} onSelect={(s) => { setSelected(s.id); }} onOpen={setOpened} onRun={run} />}
        />
      ) : null}
      <NewSetDialog open={creating} onOpenChange={setCreating} agentId={agentId} agents={agentList} canRun={can.run} />
      {opened ? <SetSheet set={opened} agentId={agentId} canManage={can.manage} onClose={() => { setOpened(null); }} /> : null}
      <CompareDialog pair={pair} agentId={agentId} agentName={agentName(agentId)} canApply={can.editAgent} onClose={() => { setPair(null); }} />
    </div>
  );
}
